import { FC } from 'react'
import { SimUploadPhase } from '../../hooks/useSimTemplateUpload'

type SimUploadStatusProps = {
  phase: SimUploadPhase
  progress: number
  error?: string | null
  /** The sim filename the run will use once landed. */
  fileName?: string
}

/**
 * Inline status + pre-submission confirmation for a per-case `.sim` upload. Makes it explicit
 * which template the run will use and whether it has been confirmed present in Azure, so a run
 * can never be submitted against a sim that never landed.
 */
const SimUploadStatus: FC<SimUploadStatusProps> = ({ phase, progress, error, fileName }) => {
  if (phase === 'idle') return null

  if (phase === 'uploading') {
    return (
      <div className="mt-2 text-xs text-gray-600 dark:text-neutral-30 flex items-center gap-2">
        <i className="pi pi-spin pi-spinner" />
        <span>Uploading to Azure… {progress}%</span>
      </div>
    )
  }

  if (phase === 'verifying') {
    return (
      <div className="mt-2 text-xs text-gray-600 dark:text-neutral-30 flex items-center gap-2">
        <i className="pi pi-spin pi-spinner" />
        <span>Confirming the SIM file landed in Azure…</span>
      </div>
    )
  }

  if (phase === 'failed') {
    return (
      <div className="mt-2 p-2 rounded border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
        <i className="pi pi-times-circle mt-0.5" />
        <span>{error || 'Upload could not be confirmed in Azure. Please re-upload.'}</span>
      </div>
    )
  }

  // landed
  return (
    <div className="mt-2 p-2 rounded border border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700 text-xs text-green-800 dark:text-green-300 flex items-start gap-2">
      <i className="pi pi-check-circle mt-0.5" />
      <span>
        Confirmed in Azure
        {fileName ? (
          <>
            : <strong>{fileName}</strong>
          </>
        ) : null}
        . This SIM will be used for the run.
      </span>
    </div>
  )
}

export default SimUploadStatus
